<template>
    <TabsRoot
        v-model="activeTab"
        :default-value="defaultValue"
        :orientation="orientation"
        :class="['tabsRoot', orientation === 'vertical' ? 'vertical' : 'horizontal', $slots.tab && 'customTabs']"
        :unmount-on-hide="unmountOnHide">
        <TabsList v-if="!hideSingleTabList || tabIds.length > 1" :class="['tabsList', listClass]">
            <TabsIndicator v-if="!$slots.tab" :class="['tabsIndicator', indicatorClass]">
                <div class="tabsIndicatorBar" />
            </TabsIndicator>
            <TabsTrigger
                v-for="tabId of tabIds"
                :key="tabId"
                ref="tabTriggers"
                :class="['tabsTrigger', $slots.tab && 'group', triggerClass]"
                :data-tab-id="tabId"
                :value="tabId"
                :disabled="disabled || disabledTabIds.includes(tabId)">
                <slot v-if="$slots.tab" name="tab" :tab-id="tabId" :selected="selectedTab === tabId" :disabled="disabled || disabledTabIds.includes(tabId)"></slot>
                <span v-else-if="counts?.[tabId] !== undefined" class="inline-flex items-center gap-2"
                    >{{ labels[tabId] }}<BaseCountBadge :count="counts[tabId]" tone="neutral" :ringed="false" class="border border-border bg-surface-raised"
                /></span>
                <template v-else>{{ labels[tabId] }}</template>
            </TabsTrigger>
        </TabsList>
        <TabsContent v-for="tabId of tabIds" :key="tabId" :value="tabId" :class="['tabsContent', contentClass]">
            <slot :name="`content-${tabId}`"></slot>
        </TabsContent>
    </TabsRoot>
</template>

<script setup lang="ts">
/**
 * Tabs with a text-only label per tab and a content slot per tabId.
 * ie. if you supply tabIds = ['insight', 'memories']
 * and labels = { insight: "Insight", memories: "Memories" }
 * then you supply slots like:
 *   <template #content-insight>...</template>
 *   <template #content-memories>...</template>
 *
 * Labels are plain strings rather than slots so tab triggers can't carry icons.
 * A tab can show a neutral count badge after its label via `counts`, e.g. { members: 5 }.
 *
 * Text labels are the default and the rule for navigation tabs. A step-style row whose tabs carry
 * an icon, a date and a state (the Moments timeline) supplies a `#tab="{ tabId, selected, disabled }"`
 * slot instead. BaseTabs still owns the tab roles, roving focus, arrow keys and selection; the slot
 * only draws the inside of each trigger, including its own selected marker, so the shared
 * underline and hairline are dropped and the tabs share the row equally. Each trigger is a
 * Tailwind `group`, so slot content can react to hover with `group-hover:`.
 * `labels` stays required in that mode and is what the slot is expected to show as the title.
 */
import BaseCountBadge from "~vue/components/BaseCountBadge.vue";
import { TabsContent, TabsIndicator, TabsList, TabsRoot, TabsTrigger } from "reka-ui";
import { computed, useTemplateRef } from "vue";

type BaseTabsProps = {
    tabIds: string[];
    labels: Record<string, string>;
    counts?: Record<string, number>;
    hideSingleTabList?: boolean;
    listClass?: string;
    indicatorClass?: string;
    triggerClass?: string;
    contentClass?: string;
    orientation?: "horizontal" | "vertical";
    disabled?: boolean;
    /** Tabs that cannot be selected, e.g. a step that has not opened yet. `disabled` disables them all. */
    disabledTabIds?: string[];
    unmountOnHide?: boolean;
};

defineSlots<{
    tab?(props: { tabId: string; selected: boolean; disabled: boolean }): unknown;
    [content: `content-${string}`]: (() => unknown) | undefined;
}>();

const {
    tabIds,
    labels,
    counts,
    hideSingleTabList = false,
    listClass = "",
    indicatorClass = "",
    triggerClass = "",
    contentClass = "",
    orientation = "horizontal",
    disabled = false,
    disabledTabIds = [],
    unmountOnHide = true,
} = defineProps<BaseTabsProps>();

const activeTab = defineModel<string>();
const defaultValue = computed(() => tabIds[0]);
// Uncontrolled, reka selects defaultValue without writing it back to the model.
const selectedTab = computed(() => activeTab.value ?? defaultValue.value);
const tabTriggers = useTemplateRef("tabTriggers");

const focusTab = (tabId: string, scrollOptions: ScrollIntoViewOptions = { behavior: "smooth", block: "start", inline: "nearest" }) => {
    const trigger = tabTriggers.value?.find((tabTrigger) => tabTrigger?.$el.dataset.tabId === tabId)?.$el;
    if (!(trigger instanceof HTMLElement)) {
        return;
    }

    trigger.focus({ preventScroll: true });
    trigger.scrollIntoView(scrollOptions);
};

defineExpose({ focusTab });
</script>

<style scoped>
@reference "../../../css/tailwind.css";

@layer components {
    .tabsRoot {
        @apply flex;
    }

    .tabsList {
        /* shrink-0: the tab header must never be flex-shrunk by a constrained
           flex-column parent. Shrinking compresses each trigger below its
           natural height, and with border-box that eats the trigger's bottom
           padding — pulling the active-tab underline up against the text
           (visibly so for labels with descenders). */
        @apply relative flex shrink-0;
    }

    .tabsIndicator {
        @apply absolute rounded-none isolate transition-[width,transform,translate] duration-300;
    }

    .tabsIndicatorBar {
        @apply bg-tabs-indicator w-full h-full;
    }

    .tabsTrigger {
        @apply body-small py-2 cursor-pointer pointer-events-auto transition-colors focus-visible:relative data-[state=active]:body-small-bold data-[state=active]:text-tabs-active-text data-[state=inactive]:enabled:hover:text-tabs-active-text disabled:text-tabs-disabled-text disabled:cursor-not-allowed;
    }

    .tabsContent {
        @apply w-full h-full focus-visible:outline-hidden;
    }

    .tabsRoot.horizontal {
        @apply flex-col;
        > .tabsList {
            @apply w-full gap-4 overflow-x-auto overflow-y-clip;
            /* An inset shadow rather than a border so the hairline never adds
               to the list's height; weight is a token because the tonal spec
               draws it thinner than the legacy 2px. */
            box-shadow: 0 calc(-1 * var(--tabs-hairline-weight)) 0 var(--color-tabs-hairline) inset;

            > .tabsIndicator {
                @apply left-0 h-0.5 bottom-0 w-(--reka-tabs-indicator-size) translate-x-(--reka-tabs-indicator-position);
            }
            > .tabsTrigger {
                @apply flex-none pb-3;
            }
        }
    }

    /* Slot-drawn tabs: the slot owns label, marker and spacing, so the trigger is a bare,
       equal-width, start-aligned box and the list neither draws the hairline nor clips
       a marker that sits on the trigger's bottom edge. */
    .tabsRoot.customTabs > .tabsList {
        @apply gap-4 overflow-visible items-stretch;
        box-shadow: none;

        > .tabsTrigger {
            @apply relative flex flex-1 min-w-0 flex-col p-0 text-start;
        }
    }

    .tabsRoot.vertical {
        @apply flex-row gap-2;
        > .tabsList {
            @apply flex-col gap-1 w-auto overflow-y-auto overflow-x-clip;
            /* Same weight token as the horizontal hairline so both
               orientations thin together under the flag. */
            box-shadow: calc(-1 * var(--tabs-hairline-weight)) 0 0 var(--color-tabs-hairline) inset;

            > .tabsIndicator {
                @apply right-0 top-0 w-0.5 h-(--reka-tabs-indicator-size) translate-y-(--reka-tabs-indicator-position);
            }
            > .tabsTrigger {
                @apply whitespace-nowrap px-4;
            }
        }
    }
}
</style>
